import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Skeleton,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { PlanOption, SubscriptionData } from '@fsp/types';
import { changePlan, fetchPlans, NoPaymentMethodError } from '../services/plans';
import { useAuth } from '../contexts/AuthContext';
import { formatQuotaValue } from '../utils/quota';
import { DiscountInfo } from './PlanSelectorCoupon';

interface PlanSelectorProps {
  currentSubscription?: SubscriptionData | null;
  onPlanChanged?: () => void | Promise<void>;
  onPaymentMethodRequired?: () => void;
  disabled?: boolean;
  showCurrentPlanChip?: boolean;
  promotionCode?: string;
  discount?: DiscountInfo | null;
}

const PlanSelector: React.FC<PlanSelectorProps> = ({
  currentSubscription,
  onPlanChanged,
  onPaymentMethodRequired,
  disabled = false,
  showCurrentPlanChip = true,
  promotionCode,
  discount,
}) => {
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [processingPlanId, setProcessingPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { refreshUserOrganisationState } = useAuth();

  // Load available plans
  const loadPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPlans();
      data.sort((a, b) => (a.amount || 0) - (b.amount || 0));
      setPlans(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load available plans');
    } finally {
      setLoading(false);
    }
  };

  // Handle plan selection
  const handleSelectPlan = async (priceId: string) => {
    try {
      setProcessingPlanId(priceId);
      setError(null);
      await changePlan(priceId, currentSubscription, promotionCode);

      // Call the callback if provided
      if (onPlanChanged) {
        await onPlanChanged();
      }
    } catch (err: any) {
      if (err instanceof NoPaymentMethodError) {
        // Call the payment method required callback if provided
        if (onPaymentMethodRequired) {
          onPaymentMethodRequired();
        } else {
          setError('Please add a payment method before subscribing');
        }
      } else {
        setError(err.message || 'Failed to change subscription');
      }
    } finally {
      setProcessingPlanId(null);
    }
  };

  useEffect(() => {
    refreshUserOrganisationState();
    loadPlans();
  }, []);

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getDiscountedAmount = (amount: number): number | null => {
    if (!discount) return null;
    if (discount.percentOff) return Math.round(amount * (1 - discount.percentOff / 100));
    if (discount.amountOff) return Math.max(0, amount - discount.amountOff * 100);
    return null;
  };

  const getPlanActionLabel = (plan: PlanOption) => {
    // No subscription yet - all plans show "Subscribe"
    if (!currentSubscription?.product || !currentSubscription?.price) {
      return 'Subscribe';
    }

    // Check if this is the current plan
    if (currentSubscription.product.name === plan.name) {
      return 'Current Plan';
    }

    // Compare prices for upgrade/downgrade
    const currentPlanAmount = currentSubscription.price.amount || 0;
    const newPlanAmount = plan.amount || 0;

    if (newPlanAmount > currentPlanAmount) return 'Upgrade';
    if (newPlanAmount < currentPlanAmount) return 'Downgrade';
    return 'Change Plan';
  };

  const getPlanActionIcon = (plan: PlanOption) => {
    // No subscription yet - show check icon for all
    if (!currentSubscription?.product || !currentSubscription?.price) {
      return <CheckCircleIcon />;
    }

    // Check if this is the current plan
    if (currentSubscription.product.name === plan.name) {
      return <CheckCircleIcon />;
    }

    // Compare prices for upgrade/downgrade icons
    const currentPlanAmount = currentSubscription.price.amount || 0;
    const newPlanAmount = plan.amount || 0;

    if (newPlanAmount > currentPlanAmount) return <ArrowUpwardIcon />;
    if (newPlanAmount < currentPlanAmount) return <ArrowDownwardIcon />;
    return <CheckCircleIcon />;
  };

  const isCurrentPlan = (plan: PlanOption) => {
    return currentSubscription?.product?.name === plan.name;
  };

  if (loading) {
    return <PlanSelectorSkeleton />;
  }

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.priceId}>
            <Box sx={{ position: 'relative', height: '100%' }}>
              {showCurrentPlanChip && isCurrentPlan(plan) && (
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    top: -12,
                  }}
                >
                  <Chip label="Current Plan" size="small" color="primary" sx={{ zIndex: 1 }} />
                </Box>
              )}
              <Card
                variant="outlined"
                sx={{
                  backgroundColor: '#fff',
                  height: '100%',
                  transition: 'all 0.2s ease-in-out',
                  ...(isCurrentPlan(plan) && {
                    borderColor: 'primary.main',
                    boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.5)',
                  }),
                }}
              >
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" gutterBottom>
                    {plan.name}
                  </Typography>
                  <Typography variant="h4" gutterBottom>
                    {plan.amount ? (
                      <>
                        {getDiscountedAmount(plan.amount) !== null ? (
                          <>
                            <Typography
                              component="span"
                              variant="h5"
                              color="text.secondary"
                              sx={{ textDecoration: 'line-through', mr: 1 }}
                            >
                              {formatCurrency(plan.amount, plan.currency || 'aud')}
                            </Typography>
                            <Typography component="span" variant="h4" color="success.main">
                              {formatCurrency(getDiscountedAmount(plan.amount)!, plan.currency || 'aud')}
                            </Typography>
                          </>
                        ) : (
                          formatCurrency(plan.amount, plan.currency || 'aud')
                        )}
                      </>
                    ) : (
                      'Custom'
                    )}
                    {plan.interval && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        /{plan.interval}
                      </Typography>
                    )}
                  </Typography>
                  {discount && getDiscountedAmount(plan.amount || 0) !== null && plan.amount && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
                      {discount.duration === 'repeating' && discount.durationInMonths
                        ? `for the first ${discount.durationInMonths} months, then ${formatCurrency(plan.amount, plan.currency || 'aud')}/${plan.interval || 'year'}`
                        : discount.duration === 'once'
                          ? `on first invoice, then ${formatCurrency(plan.amount, plan.currency || 'aud')}/${plan.interval || 'year'}`
                          : 'forever'}
                    </Typography>
                  )}
                  {plan.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {plan.description}
                    </Typography>
                  )}

                  <List dense sx={{ flexGrow: 1 }}>
                    {plan.metadata?.quotaMaxTeamMembers && (
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={`${formatQuotaValue(plan.metadata.quotaMaxTeamMembers)} team members`} />
                      </ListItem>
                    )}
                    {plan.metadata?.quotaMaxPlans && (
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={`${formatQuotaValue(plan.metadata.quotaMaxPlans)} plans`} />
                      </ListItem>
                    )}
                    {plan.metadata?.quotaAiRequests && plan.metadata.quotaAiRequests !== '0' && (
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={`${formatQuotaValue(plan.metadata.quotaAiRequests)} AI templates`} />
                      </ListItem>
                    )}
                    {(plan.features || []).map((feature, idx) => (
                      <ListItem key={idx} disableGutters>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>

                  {plan.amount ? (
                    <Button
                      variant={isCurrentPlan(plan) ? 'outlined' : 'contained'}
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={() => handleSelectPlan(plan.priceId)}
                      disabled={
                        processingPlanId === plan.priceId ||
                        isCurrentPlan(plan) ||
                        processingPlanId !== null ||
                        disabled
                      }
                      startIcon={
                        processingPlanId === plan.priceId ? (
                          <CircularProgress size={20} />
                        ) : (
                          getPlanActionIcon(plan)
                        )
                      }
                      color="primary"
                    >
                      {processingPlanId === plan.priceId
                        ? 'Processing...'
                        : getPlanActionLabel(plan)}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 2 }}
                      disabled={processingPlanId !== null || disabled}
                    >
                      Contact Sales
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

// Skeleton component for loading state
const PlanSelectorSkeleton: React.FC = () => {
  return (
    <Grid container spacing={3}>
      {/* Generate 3 skeleton plan cards */}
      {[0, 1, 2].map((index) => (
        <Grid item xs={12} md={4} key={index}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Skeleton variant="text" width={120} height={28} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={150} height={40} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="90%" height={20} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="80%" height={20} />

              {/* Features list */}
              <List dense sx={{ my: 2 }}>
                {[0, 1, 2, 3].map((featureIndex) => (
                  <ListItem key={featureIndex} disableGutters>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Skeleton variant="circular" width={20} height={20} />
                    </ListItemIcon>
                    <Skeleton variant="text" width="90%" height={20} />
                  </ListItem>
                ))}
              </List>

              {/* Action button */}
              <Skeleton variant="rectangular" width="100%" height={36} sx={{ mt: 2 }} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default PlanSelector;
